// src/utils/reservationUtils.ts
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = 'America/New_York';

/**
 * Check if restaurant is closed on a given date
 */
export const checkIfRestaurantClosed = (dateToCheck: string, siteContent: any): boolean => {
  const hours = (siteContent?.data as any)?.reservation?.info?.hours;
  if (!hours) {
    return false;
  }
  
  // Parse date parts to avoid timezone issues
  const [year, month, day] = dateToCheck.split('-').map(Number);
  const dayIndex = new Date(year, month - 1, day).getDay();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  const dayHours = hours[dayName];
  
  if (!dayHours || dayHours.is_closed) {
    return true;
  }
  
  return false;
};

/**
 * Format API time slots to EST with proper timezone handling
 * Only filters past times for TODAY (EST), shows all times for future dates
 */
export const formatStartTimesToEST = (data: any, selectedDate: string): any[] => {
  // Get current time in EST
  const nowEST = dayjs().tz(TIMEZONE);
  
  // Parse selected date in EST timezone
  const selectedDateEST = dayjs.tz(selectedDate, 'YYYY-MM-DD', TIMEZONE);
  
  // Check if selected date is TODAY in EST timezone
  const isToday = selectedDateEST.isSame(nowEST, 'day');
  
  // Process and filter time slots
  return data
    .filter((item: any) => {
      // For future dates, show ALL slots (no filtering)
      if (!isToday) {
        return true;
      }
      
      // For TODAY, only show slots that are in the future
      const slotTimeEST = dayjs.tz(item.start_time, TIMEZONE);
      return slotTimeEST.isAfter(nowEST);
    })
    .map((item: any) => {
      // Format start time to 12-hour format and preserve isAvailable property
      const startTimeEST = dayjs.tz(item.start_time, TIMEZONE);
      return {
        start_time: startTimeEST.format('h:mm A'),
        isAvailable: item.isAvailable ?? true
      };
    });
};

/**
 * Get start and end time for reservation
 * Converts selected date and time to properly formatted start/end timestamps
 */
export const getStartAndEndTime = (dateStr: any, timeStr: any) => {
  // If no time is selected, use a default time (e.g., 7:00 PM)
  const defaultTime = timeStr || '7:00 PM';
  const [time, meridian] = defaultTime.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  // Convert to 24-hour format
  if (meridian?.toLowerCase() === 'pm' && hours !== 12) {
    hours += 12;
  } else if (meridian?.toLowerCase() === 'am' && hours === 12) {
    hours = 0;
  }

  // Create Date object using local time
  const [year, month, day] = dateStr.split('-').map(Number);
  const start = new Date(year, month - 1, day, hours, minutes);
  const end = new Date(start.getTime() + 60 * 60 * 1000); // Add 1 hour

  // Format as YYYY-MM-DD HH:mm:ss
  const format = (d: any) => {
    const pad = (n: any) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return {
    startTime: format(start),
    endTime: format(end),
  };
};
